import { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'utils/moment';
import { toast } from 'react-toastify';
import apis from 'services/apis';
import storage from 'utils/sessionStorage';
import shopSelectors from 'services/selectors/shop';
import { ANYONE_ID } from 'Calendar/anyone';
import type { AxiosResponse } from 'axios';
import type { IBlockHourResItem } from 'services/types/blockHours';
import { adaptBlockHourToTimeOff, ScheduleMap } from 'TimeOffList/services/time-off-adapter';
import { monthRequestRange } from 'TimeOffList/services/format';
import { requestTimeOffRefresh } from 'TimeOffList/services/refresh-signal';
import { useCalendarTimeOffSync } from 'TimeOffList/hooks/use-calendar-sync';
import type { ITimeOffEntry } from 'TimeOffList/services/types';
import {
  IStagedRecord, DEFAULT_FROM_MIN, DEFAULT_TO_MIN,
  checkOverlaps, composeReason, canStage, buildBatchPayload, FormState,
} from '../services/staged-records';
import { clampMin, DEFAULT_TIME_STEP_MIN } from '../services/time-options';
import { DateRange, applyRangePick, setRangeEdge, singleDayRange } from '../services/date-range';

type Which = 'from' | 'to';

const todayKey = () => moment().format('YYYY-MM-DD');

export const useBatchForm = (timeStepMin: number = DEFAULT_TIME_STEP_MIN) => {
  const syncCalendar = useCalendarTimeOffSync();
  const staffsAvailable = shopSelectors.staffsAvailable();
  const schedule = shopSelectors.scheduleBooking() as ScheduleMap;

  // "Anyone" is a booking placeholder and cannot hold block hours.
  const staffs = useMemo(
    () => (staffsAvailable || []).filter(s => String(s.id) !== ANYONE_ID),
    [staffsAvailable],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
  const [viewYear, setViewYear] = useState(moment().year());
  const [viewMonth, setViewMonth] = useState(moment().month());
  // Inclusive day range for the staged record; both ends default to today, so a
  // record is single-day until the user picks a second day.
  const [range, setRange] = useState<DateRange>(() => singleDayRange(todayKey()));
  const [allDay, setAllDay] = useState(true);
  const [fromMin, setFromMin] = useState(DEFAULT_FROM_MIN);
  const [toMin, setToMin] = useState(DEFAULT_TO_MIN);
  const [openDrop, setOpenDrop] = useState<Which | null>(null);
  const [reasonChip, setReasonChip] = useState('Vacation');
  const [reasonText, setReasonText] = useState('');
  const [records, setRecords] = useState<IStagedRecord[]>([]);
  const [existingEntries, setExistingEntries] = useState<ITimeOffEntry[]>([]);
  const [creating, setCreating] = useState(false);
  const nextRecId = useRef(1);

  const { nameById, indexById, avatarById } = useMemo(() => {
    const n: Record<number, string> = {};
    const idx: Record<number, number> = {};
    const av: Record<number, string> = {};
    staffs.forEach((s, i) => { n[Number(s.id)] = s.name; idx[Number(s.id)] = i; av[Number(s.id)] = s.avatar; });
    return { nameById: n, indexById: idx, avatarById: av };
  }, [staffs]);

  const loadConflicts = useCallback((year: number, month: number) => {
    const params = monthRequestRange(year, month);
    apis.getAllBlockHoursAptList(storage.shopId.get(), params)
      .then((res: AxiosResponse<{ payload: IBlockHourResItem[] }>) => {
        const raw = res?.data?.payload || [];
        setExistingEntries(raw.map(item => adaptBlockHourToTimeOff(item, { staffName: nameById[item.staffId], schedule })));
      })
      .catch(() => setExistingEntries([]));
  }, [nameById, schedule]);

  /**
   * Reset the form back to its defaults, including the From/To range and the
   * calendar view, which all return to TODAY (not cleared) so the next record
   * starts from a usable state.
   */
  const resetForm = useCallback(() => {
    const t = moment();
    const today = t.format('YYYY-MM-DD');
    const y = t.year();
    const m = t.month();
    setSelectedStaffIds([]);
    setRange(singleDayRange(today));
    setViewYear(y);
    setViewMonth(m);
    setAllDay(true);
    setFromMin(DEFAULT_FROM_MIN);
    setToMin(DEFAULT_TO_MIN);
    setOpenDrop(null);
    setReasonChip('Vacation');
    setReasonText('');
    // Conflict data follows the month the calendar jumped back to.
    loadConflicts(y, m);
  }, [loadConflicts]);

  const openModal = useCallback(() => {
    resetForm();
    setRecords([]);
    setIsOpen(true);
  }, [resetForm]);

  const closeModal = useCallback(() => setIsOpen(false), []);

  // --- staff ---
  const toggleStaff = useCallback((id: number) => setSelectedStaffIds(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id])), []);
  const selectAllStaff = useCallback(() => setSelectedStaffIds(staffs.map(s => Number(s.id))), [staffs]);
  const clearStaff = useCallback(() => setSelectedStaffIds([]), []);

  // --- calendar ---
  const goMonth = useCallback((y: number, m: number) => { setViewYear(y); setViewMonth(m); loadConflicts(y, m); }, [loadConflicts]);
  const prevMonth = useCallback(() => goMonth(viewMonth === 0 ? viewYear - 1 : viewYear, viewMonth === 0 ? 11 : viewMonth - 1), [goMonth, viewYear, viewMonth]);
  const nextMonth = useCallback(() => goMonth(viewMonth === 11 ? viewYear + 1 : viewYear, viewMonth === 11 ? 0 : viewMonth + 1), [goMonth, viewYear, viewMonth]);

  /** Bring the calendar (and its conflict data) to the month a day key falls in. */
  const jumpToMonthOf = useCallback((k: string) => {
    const y = Number(k.slice(0, 4));
    const m = Number(k.slice(5, 7)) - 1;
    if (y !== viewYear || m !== viewMonth) {
      setViewYear(y);
      setViewMonth(m);
      // Keep conflict data aligned with the jumped-to month.
      loadConflicts(y, m);
    }
  }, [loadConflicts, viewYear, viewMonth]);

  /** Two-tap range pick: first tap opens the range, second tap closes it. */
  const pickDay = useCallback((k: string) => {
    setRange(r => applyRangePick(r, k));
    jumpToMonthOf(k);
  }, [jumpToMonthOf]);

  /** From/To inputs set one end directly, leaving the range closed. */
  const setRangeFrom = useCallback((k: string) => {
    setRange(r => setRangeEdge(r, 'start', k));
    if (k) jumpToMonthOf(k);
  }, [jumpToMonthOf]);

  const setRangeTo = useCallback((k: string) => {
    setRange(r => setRangeEdge(r, 'end', k));
    if (k) jumpToMonthOf(k);
  }, [jumpToMonthOf]);

  /** Quick pills collapse the range onto one day. */
  const pickSingleDay = useCallback((k: string) => {
    setRange(singleDayRange(k));
    jumpToMonthOf(k);
  }, [jumpToMonthOf]);

  const pickToday = useCallback(() => pickSingleDay(todayKey()), [pickSingleDay]);
  const pickTomorrow = useCallback(() => pickSingleDay(moment().add(1, 'day').format('YYYY-MM-DD')), [pickSingleDay]);

  // --- time ---
  const toggleAllDay = useCallback(() => { setAllDay(a => !a); setOpenDrop(null); }, []);
  const step = useCallback((which: Which, dir: 1 | -1) => {
    const delta = dir * timeStepMin;
    if (which === 'from') setFromMin(v => clampMin(v + delta, timeStepMin));
    else setToMin(v => clampMin(v + delta, timeStepMin));
  }, [timeStepMin]);
  const toggleDrop = useCallback((which: Which) => setOpenDrop(d => (d === which ? null : which)), []);
  const pickTime = useCallback((which: Which, value: number) => {
    if (which === 'from') setFromMin(value); else setToMin(value);
    setOpenDrop(null); setAllDay(false);
  }, []);

  // --- derived ---
  const formState: FormState = { selectedStaffIds, rangeStart: range.start, rangeEnd: range.end, allDay, fromMin, toMin };
  const conflicts = useMemo(
    () => checkOverlaps({ selectedStaffIds, rangeStart: range.start, rangeEnd: range.end, allDay, fromMin, toMin }, existingEntries),
    [selectedStaffIds, range.start, range.end, allDay, fromMin, toMin, existingEntries],
  );
  const stageable = canStage(formState);
  const selCount = selectedStaffIds.length;

  const addHint = selCount === 0
    ? 'Select staff, date & time to add'
    : stageable ? `Adds ${selCount} ${selCount > 1 ? 'records' : 'record'}` : 'Set a valid time range';
  const addLabel = selCount > 1 ? `Add ${selCount} Records` : 'Add to Selected';

  /**
   * Add is always clickable: invalid input is reported with a toast rather than
   * a disabled button, so the user is told what is missing.
   */
  const addRecords = useCallback(() => {
    if (!selectedStaffIds.length) {
      toast.error('Please select at least one staff');
      return;
    }
    if (!range.start || !range.end) {
      toast.error('Please select a date');
      return;
    }
    if (!allDay && fromMin >= toMin) {
      toast.error('Please set a valid time range');
      return;
    }

    const reason = composeReason(reasonChip, reasonText.trim());
    const added: IStagedRecord[] = selectedStaffIds.map(staffId => ({
      id: nextRecId.current++,
      staffId,
      name: nameById[staffId] || String(staffId),
      start: range.start,
      end: range.end,
      allDay,
      fromMin: allDay ? undefined : fromMin,
      toMin: allDay ? undefined : toMin,
      reason,
    }));
    setRecords(prev => [...prev, ...added]);
    resetForm();
  }, [selectedStaffIds, range.start, range.end, allDay, fromMin, toMin, reasonChip, reasonText, nameById, resetForm]);

  const removeRecord = useCallback((id: number) => setRecords(prev => prev.filter(r => r.id !== id)), []);

  const createAll = useCallback(async () => {
    if (!records.length || creating) return;
    setCreating(true);
    try {
      const res: AxiosResponse<{ payload?: unknown }> = await apis.addBlockHoursBatch(buildBatchPayload(records));
      if (res?.data?.payload !== undefined && res?.data?.payload !== null) {
        toast.success('Time off created');
        // Refresh the Time Off List (it refetches its own selected day) and the
        // outer scheduler calendar (block hours + staff columns) for the
        // currently-viewed day.
        requestTimeOffRefresh();
        syncCalendar();
        setRecords([]);
        setIsOpen(false);
      } else {
        toast.error('Failed to create time off');
      }
    } catch (e) {
      toast.error('Failed to create time off');
    } finally {
      setCreating(false);
    }
  }, [records, creating, syncCalendar]);

  return {
    isOpen, openModal, closeModal,
    staffs, indexById, avatarById, timeStepMin,
    selectedStaffIds, toggleStaff, selectAllStaff, clearStaff,
    viewYear, viewMonth,
    rangeStart: range.start, rangeEnd: range.end, pickPhase: range.phase,
    prevMonth, nextMonth, pickDay, pickToday, pickTomorrow, setRangeFrom, setRangeTo,
    allDay, fromMin, toMin, openDrop, toggleAllDay, step, toggleDrop, pickTime,
    reasonChip, reasonText, setReasonChip, setReasonText,
    conflicts, addHint, addLabel, stageable,
    records, addRecords, removeRecord, creating, createAll,
  };
};

export type UseBatchForm = ReturnType<typeof useBatchForm>;
